import { SampleProtocolInstance, ProcessingGroup } from "./ProtocolTypes";
import { Sample } from "./SampleTypes";
import type { LegacyUserId } from "../identity";

export interface SampleResult {
	id: number;
	resultable_type: 'SampleProtocolInstance' | 'AnalysisProtocolInstance' | 'ProcessingGroup' | 'Sample';
	resultable_id: number;
	result_type: ResultType;
	test_category?: string;
	test_type?: string;
	status: ResultStatus;
	data?: Record<string, any>;
	analysis_results?: Record<string, any>;
	notes?: string;
	verification_status: VerificationStatus;
	verified_by?: LegacyUserId;
	verified_at?: string;
	verification_notes?: string;
	uploaded_by: LegacyUserId;
	analyzed_at?: string;
	created_at: string;
	updated_at: string;

	// Relationships
	resultable?: SampleProtocolInstance | ProcessingGroup | Sample;

	// Computed properties
	associated_samples?: any[];
	files?: any[];
	// Parallel to `files`; null on any result uploaded before labiq stored it.
	files_meta?: ResultFileMeta[] | null;
	sample?: Sample;
}

export interface ResultFileMeta {
	file_id: string;
	name: string;
	size: number;
	mime: string;
}

export interface GeneCodesSampleSummary {
	total_snps: number;
	homozygous_count: number;
	heterozygous_count: number;
	no_call_count: number;
	// A percentage (0–100), unlike the genotyper's 0–1 fraction.
	call_rate: number;
}

export interface GeneCodesOverallStatistics {
	total_samples_processed?: number;
	total_snps_across_all_samples?: number;
	total_homozygous_calls?: number;
	total_heterozygous_calls?: number;
	total_no_calls?: number;
	average_call_rate?: number;
	min_call_rate?: number;
	max_call_rate?: number;
}

export interface GeneCodesAnalysisResults {
	analysis_type: 'gene_codes';
	processing_type?: 'group' | 'individual';
	sample_ids?: string[];
	total_samples?: number;
	records_processed?: number;
	sample_statistics?: Record<string, { summary_statistics?: GeneCodesSampleSummary }>;
	overall_statistics?: GeneCodesOverallStatistics;
	total_processing_time?: number;
	errors?: string[];
	status?: string;
}

export interface GsaRawAnalysisSummary {
	analysis_type: 'gsa_raw';
	run_id?: number | null;
	status?: string | null;
	samples?: number | null;
	snp_status?: string | null;
	analysis_status?: string | null;
	cnv_rows?: number | null;
	pgx_rows?: number | null;
	mtdna_rows?: number | null;
	no_signal?: number | null;
	warnings?: number | unknown[] | null;
	artifact_file_id?: string | null;
	error_code?: string | null;
}

export const isGeneCodesAnalysisResults = (value: unknown): value is GeneCodesAnalysisResults =>
	!!value && typeof value === 'object' && (value as { analysis_type?: unknown }).analysis_type === 'gene_codes';

export const isGsaRawAnalysisSummary = (value: unknown): value is GsaRawAnalysisSummary =>
	!!value && typeof value === 'object' && (value as { analysis_type?: unknown }).analysis_type === 'gsa_raw';

export interface SampleResultStatistics {
	total_results: number;
	pending_results: number;
	completed_results: number;
	failed_results: number;
	verified_results: number;
	rejected_results: number;
	pending_verification: number;
	results_by_type: Record<string, number>;
	results_by_category: Record<string, number>;
	results_by_processing_type?: {
		sample: number;
		analysis: number;
		group: number;
		practitioner: number;
	};
}

// Request Types
export interface UploadResultRequest {
	result_type: ResultType;
	files?: File[];
	data?: Record<string, any>;
	notes?: string;
	uploaded_by: LegacyUserId;
	auto_analyze?: boolean;
}

export interface VerifyResultRequest {
	verification_status: VerificationStatus;
	verification_notes?: string;
	verified_by: LegacyUserId;
}

export interface ResultFilter {
	status?: ResultStatus;
	verification_status?: VerificationStatus;
	test_category?: string;
	result_type?: ResultType;
	processing_type?: 'sample' | 'analysis' | 'group' | 'individual';
	sort_by?: string;
	sort_order?: 'asc' | 'desc';
	per_page?: number;
	page?: number;
	date_from?: string;
	date_to?: string;
}

export interface AnalyzeResultResponse {
	message: string;
	result: SampleResult;
	analysis_data: Record<string, any>;
	processing_type?: 'sample' | 'analysis';
}

export interface UploadResultResponse {
	message: string;
	result: SampleResult;
	processing_type?: 'sample' | 'analysis';
}

export interface PaginatedResultResponse {
	data: SampleResult[];
	total: number;
	per_page: number;
	current_page: number;
	last_page: number;
	from: number;
	to: number;
}

// Enums
export type ResultType = 'file_upload' | 'full_analysis' | 'manual_entry' | 'gene_codes' | 'blood_report' | 'gsa_raw';

export type ResultStatus =
	| 'pending'
	| 'processing'
	| 'completed'
	| 'failed'
	| 'verified'
	| 'rejected';

export type VerificationStatus = 'pending' | 'verified' | 'rejected';

export type ProcessingType = 'sample' | 'analysis' | 'group' | 'practitioner';

// Options for dropdowns
export const ResultTypeOptions = [
	{ label: "File Upload", value: "file_upload" },
	{ label: "Report Analysis", value: "full_analysis" },
	{ label: "Manual Entry", value: "manual_entry" },
	{ label: "Gene.Codes", value: "gene_codes" },
	{ label: "Blood Report", value: "blood_report" },
	{ label: "GSA Raw (AGRF)", value: "gsa_raw" }
];

export const ResultStatusOptions = [
	{ label: "Pending", value: "pending" },
	{ label: "Processing", value: "processing" },
	{ label: "Completed", value: "completed" },
	{ label: "Failed", value: "failed" },
	{ label: "Verified", value: "verified" },
	{ label: "Rejected", value: "rejected" }
];

export const VerificationStatusOptions = [
	{ label: "Pending Verification", value: "pending" },
	{ label: "Verified", value: "verified" },
	{ label: "Rejected", value: "rejected" }
];

export const ProcessingTypeOptions = [
	{ label: "Sample Processing", value: "sample" },
	{ label: "Analysis Processing", value: "analysis" },
	{ label: "Group Processing", value: "group" },
	{ label: "Practitioner Upload", value: "practitioner" }
];

// Default values
export const defaultSampleResult: Partial<SampleResult> = {
	result_type: 'file_upload',
	status: 'pending',
	verification_status: 'pending',
	files: [],
	data: {},
	analysis_results: {},
	notes: ''
};

export const defaultUploadResultRequest: Partial<UploadResultRequest> = {
	result_type: 'file_upload',
	files: [],
	data: {},
	notes: '',
	auto_analyze: true
};

export const defaultResultFilter: ResultFilter = {
	sort_by: 'created_at',
	sort_order: 'desc',
	per_page: 15,
	page: 1
};

// Helper functions
export const getProcessingTypeLabel = (type: ProcessingType): string => {
	switch (type) {
		case 'sample':
			return 'Sample Processing';
		case 'analysis':
			return 'Analysis Processing';
		case 'group':
			return 'Group Processing';
		case 'practitioner':
			return 'Practitioner Upload';
		default:
			return 'Unknown';
	}
};

export const getResultTypeLabel = (type: ResultType): string => {
	const option = ResultTypeOptions.find(opt => opt.value === type);
	return option?.label || type;
};

export const getStatusLabel = (status: ResultStatus): string => {
	const option = ResultStatusOptions.find(opt => opt.value === status);
	return option?.label || status;
};

export const getVerificationStatusLabel = (status: VerificationStatus): string => {
	const option = VerificationStatusOptions.find(opt => opt.value === status);
	return option?.label || status;
};

export const isResultCompleted = (result: SampleResult): boolean => {
	return result.status === 'completed' || result.status === 'verified';
};

export const isResultPending = (result: SampleResult): boolean => {
	return result.status === 'pending' || result.status === 'processing';
};

export const isResultFailed = (result: SampleResult): boolean => {
	return result.status === 'failed' || result.status === 'rejected';
};

export const needsVerification = (result: SampleResult): boolean => {
	return result.status === 'completed' && result.verification_status === 'pending';
};